import Modal from "../../../../../../components/base/Modal";
import type { ThemeClasses, TripFormData, TripAssignmentFormData, TimezoneCode } from "../types";
import { TIMEZONE_OPTIONS } from "../types";
import type { UseFormRegister, FieldErrors, UseFormSetValue } from "react-hook-form";
import type { TFunction } from "i18next";
import Select from "react-select";
import { getCustomSelectStyles } from "../../../../shared";
import { useRoutesStore } from "../../../../../../store/admin/routesStore";
import { useUsersStore } from "../../../../../../store/admin/usersStore";
import { useAllDevices } from "../../../../../../features/devices/queries";
import { useStatuses } from "../../../../../../features/catalogs/queries";
import { RouteSummary } from "./RouteSummary";
import { useEffect, useState } from "react";
import { getStatusOptionsForEntity } from "../../../../../../utils/getStatusEntity";
import { useIsReseller } from "../../../../../../hooks/useIsReseller";

interface TripAddModalProps {
  themeClasses: ThemeClasses;
  isDarkMode: boolean;
  register: UseFormRegister<TripFormData>;
  errors: FieldErrors<TripFormData>;
  loadingSubmit: boolean;
  onClose: () => void;
  onSubmit: (e?: React.BaseSyntheticEvent) => Promise<void>;
  t: TFunction;
  setValue: UseFormSetValue<TripFormData>;
  watchedValues: Partial<TripFormData>;
}

export function TripAddModal({
  themeClasses,
  isDarkMode,
  register,
  errors,
  loadingSubmit,
  onClose,
  onSubmit,
  t,
  setValue,
  watchedValues,
}: TripAddModalProps) {
  const inputClass = `w-full px-3 py-2 ${isDarkMode ? "bg-dark-card" : "bg-gray-50"} border ${themeClasses.cardBorder} rounded-lg ${themeClasses.text} focus:outline-none focus:ring-2 focus:ring-primary-500`;
  const labelClass = `block text-sm font-medium ${themeClasses.text} mb-1`;

  const routes = useRoutesStore((state) => state.routes || []);
  const fetchRoutes = useRoutesStore((state) => state.fetchRoutes);

  const { data: devicesList = [] } = useAllDevices();
  const { usersLight: usersList, fetchUsersLight } = useUsersStore();
  const { data: statuses = [] } = useStatuses();
  const { isReseller } = useIsReseller();

  const [assignments, setAssignments] = useState<TripAssignmentFormData[]>([]);
  const [newAssignment, setNewAssignment] = useState<Partial<TripAssignmentFormData>>({
    device_id: "",
    start_time: "",
    end_time: "",
    timezone: "America/Mexico_City",
  });

  // Timezone options for select
  const timezoneOptions = Object.entries(TIMEZONE_OPTIONS).map(([value, label]) => ({
    value: value as TimezoneCode,
    label,
  }));

  useEffect(() => {
    fetchRoutes({ limit: 500, page: 1 });
    // Resellers can assign carriers from any tenant; bypass the company filter
    // so the dropdown lists every user instead of just the active company.
    fetchUsersLight({ allCompanies: isReseller });
  }, [fetchRoutes, fetchUsersLight, isReseller]);

  // Get status options for trips entity
  const statusOptions = getStatusOptionsForEntity(statuses, "trips", t);

  useEffect(() => {
    setValue("assignments", assignments);
  }, [assignments, setValue]);

  const selectedRoute = routes.find((r) => r.id === watchedValues.route_id) || null;

  const deviceOptions = devicesList.map((d) => {
    const vehicleName = d.vehicle ? d.vehicle?.name : "";
    const trailerName = d.remolque ? d.remolque?.name : "";
    const nameDevice = vehicleName || trailerName ? d.identifier : d.name;
    return {
      value: d.id,
      label: `${nameDevice} ${vehicleName ? `(${vehicleName})` : ""} ${trailerName ? `(${trailerName})` : ""}`.trim(),
    };
  });

  const userOptions = usersList.map((u) => ({
    value: u.id,
    label: `${u.first_name} ${u.last_name} (${u.email})`,
  }));

  const routeOptions = routes.map((r: { id: string; name?: string }) => ({
    value: r.id,
    label: r.name || r.id,
  }));

  const priorityOptions = [
    { value: "low", label: t("trips.priority.low") },
    { value: "medium", label: t("trips.priority.medium") },
    { value: "high", label: t("trips.priority.high") },
    { value: "urgent", label: t("trips.priority.urgent") },
  ];

  const handleAddAssignment = () => {
    if (!newAssignment.device_id || !newAssignment.start_time || !newAssignment.end_time) {
      return;
    }

    // Extract date from start_time (format: YYYY-MM-DDTHH:mm)
    const date = newAssignment.start_time.split('T')[0] || "";

    const assignment: TripAssignmentFormData = {
      device_id: newAssignment.device_id,
      date: date,
      start_time: newAssignment.start_time,
      end_time: newAssignment.end_time,
      status: "pending",
      timezone: newAssignment.timezone || "America/Mexico_City",
    };

    setAssignments([...assignments, assignment]);
    setNewAssignment({
      device_id: "",
      start_time: "",
      end_time: "",
      timezone: "America/Mexico_City",
    });
  };

  const handleRemoveAssignment = (index: number) => {
    setAssignments(assignments.filter((_, i) => i !== index));
  };

  const getDeviceLabel = (deviceId: string) => {
    const device = devicesList.find((d) => d.id === deviceId);
    if (!device) return deviceId;
    const vehicleName = device.vehicle ? device.vehicle?.name : "";
    const trailerName = device.remolque ? device.remolque?.name : "";
    return `${device.name || device.identifier} ${vehicleName ? `(${vehicleName})` : ""} ${trailerName ? `(${trailerName})` : ""}`.trim();
  };

  const formatDateTime = (datetime: string) => {
    if (!datetime) return "";
    const date = new Date(datetime);
    return date.toLocaleString("es-MX", { 
      day: "2-digit", 
      month: "2-digit", 
      year: "numeric",
      hour: "2-digit", 
      minute: "2-digit" 
    });
  };

  return (
    <Modal
      title={t("trips.modal.addTitle")}
      onClose={onClose}
      themeClasses={themeClasses}
      width="w-full sm:w-5/7"
      content={
        <form onSubmit={onSubmit} className="space-y-4 max-h-[70vh] overflow-y-auto px-2">
          {/* Basic Info */}
          <h3 className={`text-sm font-semibold ${themeClasses.text}`}>
            {t("trips.sections.basicInfo")}
          </h3>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className={labelClass}>{t("trips.fields.title")} <span className="text-red-500">*</span></label>
              <input
                {...register("title", { required: t("trips.validation.titleRequired") })}
                className={`${inputClass} ${errors.title ? "border-red-500" : ""}`}
                placeholder={t("trips.fields.title")}
              />
              {errors.title && (
                <span className="text-red-500 text-xs">{errors.title.message as string}</span>
              )}
            </div>
            <div>
              <label className={labelClass}>{t("trips.fields.status")} <span className="text-red-500">*</span></label>
              <input type="hidden" {...register("status_id", { required: t("trips.validation.statusRequired") })} />
              <Select
                options={statusOptions}
                styles={getCustomSelectStyles(isDarkMode)}
                placeholder={t("trips.modal.selectStatus")}
                value={statusOptions.find((opt) => opt.value === watchedValues.status_id) || null}
                onChange={(option: any) => setValue("status_id", option?.value || "", { shouldValidate: true })}
                isSearchable
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
              {errors.status_id && (
                <span className="text-red-500 text-xs">{errors.status_id.message as string}</span>
              )}
            </div>
            <div>
              <label className={labelClass}>{t("trips.fields.priority")} <span className="text-red-500">*</span></label>
              <input type="hidden" {...register("priority", { required: t("trips.validation.priorityRequired") })} />
              <Select
                options={priorityOptions}
                styles={getCustomSelectStyles(isDarkMode)}
                placeholder={t("trips.modal.selectPriority")}
                value={priorityOptions.find((opt) => opt.value === watchedValues.priority) || null}
                onChange={(option: any) => setValue("priority", option?.value || "", { shouldValidate: true })}
                isSearchable
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
              {errors.priority && (
                <span className="text-red-500 text-xs">{errors.priority.message as string}</span>
              )}
            </div>
          </div>

          <div>
            <label className={labelClass}>{t("trips.fields.description")}</label>
            <textarea
              {...register("description")}
              className={`${inputClass} min-h-[80px]`}
              placeholder={t("trips.fields.description")}
            />
          </div>

          {/* Route Info */}
          <h3 className={`text-sm font-semibold ${themeClasses.text} mt-6`}>
            {t("trips.sections.routeInfo")}
          </h3>

          <div>
            <label className={labelClass}>{t("trips.fields.route")} <span className="text-red-500">*</span></label>
            <input type="hidden" {...register("route_id", { required: t("trips.validation.routeRequired") })} />
            <Select
              options={routeOptions}
              styles={getCustomSelectStyles(isDarkMode)}
              placeholder={t("trips.modal.selectRoute")}
              value={routeOptions.find((opt: { value: string }) => opt.value === watchedValues.route_id) || null}
              onChange={(option: any) => setValue("route_id", option?.value || "", { shouldValidate: true })}
              isSearchable
              menuPortalTarget={document.body}
              menuPosition="fixed"
            />
            {errors.route_id && (
              <span className="text-red-500 text-xs">{errors.route_id.message as string}</span>
            )}
            {selectedRoute && (
              <RouteSummary
                route={selectedRoute}
                themeClasses={themeClasses}
                isDarkMode={isDarkMode}
                t={t}
              />
            )}
          </div>

          {/* Device Assignments */}
          <h3 className={`text-sm font-semibold ${themeClasses.text} mt-6`}>
            {t("trips.assignments.title")}
          </h3>

          {/* Add new assignment - compact layout */}
          <div className={`p-3 rounded-lg border ${themeClasses.cardBorder} ${isDarkMode ? "bg-dark-card/50" : "bg-gray-50"}`}>
            <div className="grid grid-cols-1 sm:grid-cols-12 gap-2 sm:items-end">
              {/* Device */}
              <div className="sm:col-span-3">
                <label className={`text-xs ${themeClasses.textSecondary} mb-1 block`}>{t("trips.assignments.device")}</label>
                <Select
                  options={deviceOptions}
                  styles={getCustomSelectStyles(isDarkMode)}
                  placeholder={t("trips.assignments.selectDevice")}
                  value={deviceOptions.find((opt) => opt.value === newAssignment.device_id) || null}
                  onChange={(option: any) => setNewAssignment({ ...newAssignment, device_id: option?.value || "" })}
                  isSearchable
                  isClearable
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                />
              </div>
              {/* Start DateTime */}
              <div className="sm:col-span-2">
                <label className={`text-xs ${themeClasses.textSecondary} mb-1 block`}>{t("trips.assignments.startTime")}</label>
                <input
                  type="datetime-local"
                  value={newAssignment.start_time || ""}
                  onChange={(e) => setNewAssignment({ ...newAssignment, start_time: e.target.value })}
                  className={`${inputClass} text-sm`}
                />
              </div>
              {/* End DateTime */}
              <div className="sm:col-span-2">
                <label className={`text-xs ${themeClasses.textSecondary} mb-1 block`}>{t("trips.assignments.endTime")}</label>
                <input
                  type="datetime-local"
                  value={newAssignment.end_time || ""}
                  onChange={(e) => setNewAssignment({ ...newAssignment, end_time: e.target.value })}
                  className={`${inputClass} text-sm`}
                />
              </div>
              {/* Timezone */}
              <div className="sm:col-span-3">
                <label className={`text-xs ${themeClasses.textSecondary} mb-1 block`}>{t("trips.assignments.timezone")}</label>
                <Select
                  options={timezoneOptions}
                  styles={getCustomSelectStyles(isDarkMode)}
                  placeholder={t("trips.assignments.selectTimezone")}
                  value={timezoneOptions.find((opt) => opt.value === newAssignment.timezone) || timezoneOptions.find((opt) => opt.value === "America/Mexico_City")}
                  onChange={(option: any) => setNewAssignment({ ...newAssignment, timezone: option?.value || "America/Mexico_City" })}
                  isSearchable
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                />
              </div>
              {/* Button */}
              <div className="sm:col-span-2">
                <button
                  type="button"
                  onClick={handleAddAssignment}
                  disabled={!newAssignment.device_id || !newAssignment.start_time || !newAssignment.end_time}
                  className="w-full px-3 py-2 bg-primary-500 hover:bg-primary-400 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-1"
                >
                  <i className="ri-add-line" />
                  {t("trips.assignments.add")}
                </button>
              </div>
            </div>
          </div>

          {/* Assignments list - compact cards */}
          {assignments.length > 0 && (
            <div className="space-y-2">
              {assignments.map((assignment, index) => (
                <div
                  key={index}
                  className={`flex items-center justify-between p-2 rounded-lg border ${themeClasses.cardBorder} ${isDarkMode ? "bg-dark-card/30" : "bg-gray-50"}`}
                >
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-medium ${themeClasses.text} truncate`}>
                      {getDeviceLabel(assignment.device_id)}
                    </p>
                    <p className={`text-xs ${themeClasses.textSecondary}`}>
                      {formatDateTime(assignment.start_time)} - {formatDateTime(assignment.end_time)}
                      {assignment.timezone && (
                        <span className="ml-2 text-primary-500">
                          <i className="ri-time-zone-line mr-0.5" />
                          {TIMEZONE_OPTIONS[assignment.timezone] || assignment.timezone}
                        </span>
                      )}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleRemoveAssignment(index)}
                    className="ml-2 p-1.5 text-red-500 hover:bg-red-500/10 rounded transition-colors"
                  >
                    <i className="ri-delete-bin-line" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Notified Users */}
          <h3 className={`text-sm font-semibold ${themeClasses.text} mt-6`}>
            {t("trips.sections.notifications")}
          </h3>

          <div className="flex flex-col gap-1.5">
            <label className={labelClass}>{t("trips.fields.notifiedUsers")}</label>
            <p className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"} -mt-1 mb-1`}>
              {t("trips.fields.notifiedUsersHint")}
            </p>
            <Select
              isMulti
              closeMenuOnSelect={false}
              hideSelectedOptions={false}
              options={userOptions}
              styles={getCustomSelectStyles(isDarkMode)}
              placeholder={t("trips.modal.selectUsers")}
              value={((watchedValues.notifiedUsers as string[] | undefined) || []).map((val) => {
                const existing = userOptions.find((o) => o.value === val);
                return existing || { value: val, label: val };
              })}
              onChange={(selected) =>
                setValue(
                  "notifiedUsers",
                  selected ? (selected as { value: string }[]).map((s) => s.value) : []
                )
              }
              isClearable
              isSearchable
              noOptionsMessage={() => t("trips.modal.noUsersAvailable")}
              menuPortalTarget={document.body}
              menuPosition="fixed"
            />
          </div>

          {/* Envío de correos además del push */}
          <div className="flex flex-col gap-2">
            <label className={labelClass}>{t("trips.fields.sendEmails")}</label>
            <p className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"} -mt-1 mb-1`}>
              {t("trips.fields.sendEmailsHint")}
            </p>
            {(() => {
              const active = watchedValues.sendEmails === true;
              return (
                <button
                  type="button"
                  role="switch"
                  aria-checked={active}
                  onClick={() => setValue("sendEmails", !active)}
                  className={`flex items-center justify-between gap-3 px-4 py-2.5 rounded-xl border text-sm font-medium transition-all ${
                    active
                      ? "bg-primary-500/10 border-primary-400 text-primary-600"
                      : `${themeClasses.cardBg} ${themeClasses.cardBorder} ${themeClasses.text} ${themeClasses.buttonHover}`
                  }`}
                >
                  <span className="flex items-center gap-2">
                    <i className="ri-mail-send-line 2xl:text-lg" aria-hidden />
                    {active
                      ? t("trips.fields.sendEmailsOn")
                      : t("trips.fields.sendEmailsOff")}
                  </span>
                  <span
                    className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors ${
                      active ? "bg-primary-500" : "bg-gray-400/50"
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        active ? "translate-x-4" : "translate-x-0.5"
                      }`}
                    />
                  </span>
                </button>
              );
            })()}
          </div>

          {/* Comment */}
          <div>
            <label className={labelClass}>{t("trips.fields.comment")}</label>
            <textarea
              {...register("comment")}
              className={`${inputClass} min-h-[60px]`}
              placeholder={t("trips.fields.comment")}
            />
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className={`px-4 py-2 ${themeClasses.buttonHover} border ${themeClasses.cardBorder} rounded-lg ${themeClasses.buttonText} font-medium transition-colors`}
            >
              {t("trips.actions.cancel")}
            </button>
            <button
              type="submit"
              disabled={loadingSubmit}
              className="px-4 py-2 bg-primary-500 hover:bg-primary-400 text-white rounded-lg font-medium transition-colors disabled:opacity-50"
            >
              {loadingSubmit ? t("trips.actions.creating") : t("trips.actions.create")}
            </button>
          </div>
        </form>
      }
    />
  );
}
